
import { defineComponent, PropType } from 'vue'

import { AuditLogLevel } from '~/types/auditLog'
import { AuditLog } from '~/types/types'

import { dateRangeOptions, formatDate, getDateRange, parseISODate } from '~/utils/date'

export default defineComponent({
  name: 'AuditLogPage',
  props: {
    pageNumber: {
      type: Number,
      required: true
    },
    totalCount: {
      type: Number,
      default: 0
    },
    auditLogListLoading: {
      type: Boolean,
      default: false
    },
    exportLogsLoading: {
      type: Boolean,
      default: false
    },
    isInitialPageLoad: {
      type: Boolean,
      default: false
    },
    showExportLogsSuccessModal: {
      type: Boolean,
      default: false
    },
    dateRange: {
      type: String,
      required: true
    },
    level: {
      type: String as PropType<AuditLogLevel>,
      required: true
    },
    searchQuery: {
      type: String,
      required: true
    },
    viewerEmail: {
      type: String,
      required: true
    },
    auditLogItems: {
      type: Array as PropType<Array<AuditLog>>,
      required: true
    }
  },
  data() {
    return {
      perPageCount: 30,
      dateRangeOptions
    } as {
      readonly perPageCount: number
      readonly dateRangeOptions: typeof dateRangeOptions
    }
  },
  computed: {
    auditLogItemsCount(): number {
      return this.auditLogItems.length
    },
    endDate(): string {
      const { endDate } = getDateRange(this.dateRange)
      return formatDate(parseISODate(endDate), 'll')
    },
    pageContextText(): string {
      if (this.auditLogListLoading && !this.isInitialPageLoad) {
        return 'Searching logs...'
      }

      if (this.searchQuery) {
        return `Logs containing ${this.searchQuery}`
      }

      const { count, durationType } = dateRangeOptions[this.dateRange]
      const dateRangeText = `Last ${count} ${durationType}`

      return `Logs for ${dateRangeText} ${this.startDate} - ${this.endDate}`
    },
    pageTitle(): string {
      const pageTitleMap: Record<AuditLogLevel, string> = {
        [AuditLogLevel.Repository]: 'Track activity and changes in your repository.',
        [AuditLogLevel.Team]: 'Track activity and changes in your team.'
      }
      return pageTitleMap[this.level]
    },
    showPaginationElement(): boolean {
      return this.totalCount > this.perPageCount
    },
    startDate(): string {
      const { startDate } = getDateRange(this.dateRange)
      return formatDate(parseISODate(startDate), 'll')
    }
  },
  methods: {
    updateDateRange(dateRangeFromEmit: string): void {
      this.$emit('update-filter', {
        dateRangeFromEmit,
        pageNumberFromEmit: 1 // Reset page number to `1`
      })
    },
    updatePageNumber(pageNumberFromEmit: number): void {
      this.$emit('update-filter', {
        pageNumberFromEmit
      })
    },
    updateSearchQuery(searchQueryFromEmit: string): void {
      this.$emit('update-filter', {
        searchQueryFromEmit,
        pageNumberFromEmit: 1 // Reset page number to `1`
      })
    }
  }
})
